import { Injectable } from '@angular/core';
import { Observable, Subject } from 'rxjs';
import { EventEditScope } from '@shared/service-proxies/service-proxies';

/** A client (person/company) to prefill on a NEW event opened from a client record. Structurally the
 *  same as the client-picker's `CreatedClient`; declared here so this root service doesn't import from a
 *  feature component. The form resolves the person's email (for the attendee auto-add) from its own
 *  contacts lookup by id, so no email need be carried here. */
export interface EventFormClient {
    id: number;
    name: string;
    kind: 'person' | 'company';
}

/** A request to open the reusable create/update event form (`vf-event-form-panel`). */
export interface EventFormRequest {
    /** An existing event id to edit, or null/undefined to create a new one. */
    eventId?: number | null;
    /** Date to prefill a new event with (ignored when editing). */
    date?: Date | null;
    /** Project to prefill the Case/Project field with (e.g. when opened from a project modal). */
    projectId?: number | null;
    projectName?: string | null;
    /** Client to prefill on a NEW event (opened from a Client Person/Company record). Ignored when a
     *  project is also supplied (the project locks the client) or when editing. */
    client?: EventFormClient | null;
    /** Which occurrences of a recurring series the edit applies to (chosen in the detail panel's
     *  3-scope menu). Null/undefined for a create or a non-recurring edit (the host defaults to AllEvents). */
    scope?: EventEditScope | null;
}

/** A request to open the read-only detail panel (`vf-event-detail-panel`). */
export interface EventDetailRequest {
    eventId: number;
    /** When true, opening the detail panel must NOT close an open client (contact/company) modal — the
     *  panel simply stacks above it (Events tab: opening a meeting keeps the client record open). */
    keepClientOpen?: boolean;
    /** Smart Scheduling check-in 5 (CN-02): open straight into the panel's own cancel prompt ("Cancel this event? Keep /
     *  Cancel event"), when the viewer may cancel it. Nothing is cancelled until the user confirms. */
    openCancel?: boolean;
}

/**
 * Decouples the *trigger* to open the create/update event form (e.g. the global "Create new → Event"
 * menu, or any other surface) from whichever route-shell currently *hosts* the panel — so opening an
 * event never forces a route change (e.g. away from the project modal). The active shell
 * (`AgendaComponent` for my-day/projects/contacts, `WorkspaceCalendarComponent` for the calendar route)
 * subscribes to `requests$` and shows its hosted `vf-event-form-panel`.
 *
 * Deliberately a small, single-purpose service rather than another `DataService` event-bus channel
 * (STANDARDS: don't grow the DataService event-bus).
 */
@Injectable({ providedIn: 'root' })
export class EventFormService {
    private readonly _requests = new Subject<EventFormRequest>();

    /** Hosts subscribe to this to open their panel on demand. */
    get requests$(): Observable<EventFormRequest> {
        return this._requests.asObservable();
    }

    private readonly _detailRequests = new Subject<EventDetailRequest>();

    /** Hosts subscribe to this to open the read-only detail panel (`vf-event-detail-panel`) on demand. */
    get detailRequests$(): Observable<EventDetailRequest> {
        return this._detailRequests.asObservable();
    }

    /** Open the form to create a new event. `options.date` prefills the date; `options.projectId` /
     *  `options.projectName` prefill the Case/Project field (applied once an event type that shows the
     *  project field is selected); `options.client` prefills the Client (and, for a person, auto-adds them
     *  as a required attendee once a client-bearing type is selected). */
    createEvent(options?: {
        date?: Date | null;
        projectId?: number | null;
        projectName?: string | null;
        client?: EventFormClient | null;
    }): void {
        this._requests.next({
            eventId: null,
            date: (options && options.date) || null,
            projectId: (options && options.projectId) || null,
            projectName: (options && options.projectName) || null,
            client: (options && options.client) || null,
        });
    }

    /** Open the form to edit an existing event. `scope` (from the detail panel's recurring-edit menu)
     *  tells the form which occurrences the edit applies to; omitted for a non-recurring edit. */
    editEvent(eventId: number, scope?: EventEditScope | null): void {
        this._requests.next({ eventId, date: null, scope: scope || null });
    }

    /** Open the read-only detail panel (`vf-event-detail-panel`) for an existing event. The panel's Edit
     *  action routes back through `editEvent`, so view → edit needs no extra wiring at the call site.
     *  `options.keepClientOpen` (Events tab) keeps an open client modal open beneath the panel; `options.openCancel` (the
     *  composer's "Cancel meeting" pill) opens the panel's cancel prompt. */
    viewEvent(eventId: number, options?: { keepClientOpen?: boolean; openCancel?: boolean }): void {
        this._detailRequests.next({
            eventId,
            keepClientOpen: !!(options && options.keepClientOpen),
            openCancel: !!(options && options.openCancel),
        });
    }
}
